import {FunctionComponent, useState, useEffect} from 'react'
import PropTypes,{InferProps} from 'prop-types'
import {Navbar, Nav, Button, Container, Card, Row, Col, Form, FormGroup, FormLabel, FormControl} from 'react-bootstrap'
import * as d3 from 'd3'
import { FaPowerOff } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import Spinner  from 'react-bootstrap/Spinner'

import {checkLicenseOpenSankeyPlus, checkLicenseSankeySuite, registerNewLicenseOpenSankeyPlus, registerNewLicenseSankeySuite} from '../LicenseCheckIn/LicenseFunctions'
import {logOutUser, activateLicensesTokens} from '../Login/LoginFunctions'
import { SankeyDataPropTypes }  from 'open-sankey/dist/types'

// UserData interface
interface UserData {
  count: number
  loading: boolean
  firstname: string
  name: string
  id: string
  loading_opensankeyplus: boolean
  license_opensankeyplus_id: string
  license_opensankeyplus_active: string
  license_opensankeyplus_validity: string
  loading_sankeysuite: boolean
  license_sankeysuite_id: string
  license_sankeysuite_active: string
  license_sankeysuite_validity: string
}


// Account
const AccountPropTypes = {
  navbar_logo: PropTypes.string.isRequired,
  navbar_logo_width: PropTypes.number,
  navbar_app_version: PropTypes.string.isRequired,
  token: PropTypes.bool.isRequired,
  setOpenSankeyPlus: PropTypes.func.isRequired,
  setSankeySuite: PropTypes.func.isRequired,
  setSankeyDev: PropTypes.func.isRequired,
  unsetTokens: PropTypes.func.isRequired,
  useNavigate: PropTypes.func.isRequired,
  data: PropTypes.shape(SankeyDataPropTypes).isRequired,
  set_data:PropTypes.func.isRequired
}
type AccountTypes = InferProps<typeof AccountPropTypes>

const Account:FunctionComponent<AccountTypes>=({
  navbar_logo, navbar_logo_width, navbar_app_version,
  token,
  setOpenSankeyPlus,
  setSankeySuite,
  setSankeyDev,
  unsetTokens,
  useNavigate,data,set_data})=>
{
  // Initialise traduction function
  const {t} = useTranslation()

  // Define navigation behaviour to return to App
  const navigate=useNavigate()
  const returnToApp=()=>{
    navigate('/')
    set_data({...data})
  }

  //If we acces this page without being logged, it is resent to the application
  if(!token){
    // returnToApp()
    navigate('/')
    set_data({...data})

  }

  //If we are logged the the following behaviors are defined
  //Return to Dashboard
  const returnToDashboard=()=>{
    navigate('/dashboard')
  }

  //Logout
  const loginOut=()=>{
    logOutUser(unsetTokens)
    returnToApp()
  }

  // Activate and save a new license OpenSankey+
  const signupNewLicenseOpenSankeyPlus=()=>{
    // Check licence and activate from EDD
    if (newLicenseOpenSankeyPlusToCheck) {
      registerNewLicenseOpenSankeyPlus(newLicenseOpenSankeyPlus)
      .then( () => {
        // Save in db
        const path = window.location.origin
        const url = path + '/user_infos/license_opensankeyplus'
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            license_id: newLicenseOpenSankeyPlus,
          })
        }).then( response => {
          if ( response.ok ) {
            return response.json()
          } else {
            return Promise.reject( response )
          }
        }).then(data => {
          // console.log('POST /user_infos/license_opensankeyplus : SUCCESS - ', data.message)
          let userData_ = userData
          userData_.loading_opensankeyplus = true
          userData_.license_opensankeyplus_id = newLicenseOpenSankeyPlus
          userData_.license_opensankeyplus_active = ''
          userData_.license_opensankeyplus_validity = ''
          setUserData(userData_)
          setReqCount(1)
          activateLicensesTokens(setOpenSankeyPlus, setSankeySuite, setSankeyDev) //Update tokens
        }).catch(error =>
          console.log('POST /user_infos/license_opensankeyplus : ERROR - ', error)
        )
      })
      .catch( error => {
        console.log('signupNewLicenseOpenSankeyPlus : ERROR - ', error)
      })
    }
  }

  // Activate and save a new license SankeySuite
  const signupNewLicenseSankeySuite=()=>{
    // Check licence and activate from EDD
    registerNewLicenseSankeySuite(newLicenseSankeySuite)
    .then( () => {
      // Save in db
      const path = window.location.origin
      const url = path + '/user_infos/license_sankeysuite'
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          license_id: newLicenseSankeySuite,
        })
      }).then( response => {
        if ( response.ok ) {
          return response.json()
        } else {
          return Promise.reject( response )
        }
      }).then( data_resp => {
        // console.log('POST /user_infos/license_sankeysuite : SUCCESS - ', data_resp.message)
        let userData_ = userData
        userData_.loading_sankeysuite = true
        userData_.license_sankeysuite_id = newLicenseSankeySuite
        userData_.license_sankeysuite_active = ''
        userData_.license_sankeysuite_validity = ''
        setUserData(userData_)
        setReqCount(1)
        activateLicensesTokens(setOpenSankeyPlus, setSankeySuite, setSankeyDev) //Update tokens
      }).catch(error =>
        console.log('POST /user_infos/license_sankeysuite : ERROR - ', error)
      )
    })
    .catch( error => {
      console.log('signupNewLicenseSankeySuite : ERROR - ', error)
    })
  }

  let userDataDefault: UserData = {
    count: 0,
    loading: true,
    firstname: '-',
    name: '-',
    id: '-',
    loading_opensankeyplus: true,
    license_opensankeyplus_id: '',
    license_opensankeyplus_active: '',
    license_opensankeyplus_validity: '',
    loading_sankeysuite: true,
    license_sankeysuite_id: '',
    license_sankeysuite_active: '',
    license_sankeysuite_validity: ''
  }

  // Hooks
  const [userData, setUserData] = useState(userDataDefault)
  const [reqCount, setReqCount] = useState(1)
  const [newLicenseOpenSankeyPlus, setNewLicenseOpenSankeyPlus] = useState('')
  const [newLicenseOpenSankeyPlusToCheck, setNewLicenseOpenSankeyPlusToCheck] = useState(false)
  const [newLicenseSankeySuite, setNewLicenseSankeySuite] = useState('')
  const [newLicenseSankeySuiteToCheck, setNewLicenseSankeySuiteToCheck] = useState(false)

  // Get user's data
  useEffect( () => {
    // Display error on screen
    d3.select('.LogError').selectAll('*').remove()

    // Get user data if we dont have
    if (userData.loading === true && reqCount < 20)
    {
      let reqCount_ = reqCount
      // Get user's infos
      const path = window.location.origin
      const url = path + '/user_infos'
      fetch(url)
      .then(response => {
        if ( response.ok ) {
          return response.json()
        } else {
          return Promise.reject( response )
        }
      }).then(data => {
        // User data
        let userData_ = userData
        userData_.name = data.name
        userData_.firstname = data.firstname
        userData_.id = data.email
        if (data.license_opensankeyplus !== "" &&
            data.license_opensankeyplus !== "0000" &&
            typeof(data.license_opensankeyplus) !== 'undefined')
          userData_.license_opensankeyplus_id = data.license_opensankeyplus
        else
          userData_.license_opensankeyplus_id = "-"
        if (data.license_sankeysuite !== "" &&
            data.license_sankeysuite !== "0000" &&
            typeof(data.license_sankeysuite) !== 'undefined')
          userData_.license_sankeysuite_id = data.license_sankeysuite
        else
          userData_.license_sankeysuite_id = "-"
        userData_.loading = false
        setUserData(userData_)
        // Increase number of requests
        reqCount_ = reqCount_ + 1
        setReqCount(reqCount_)
      }).catch( error => {
        // Erreur fetch user data
        console.log( 'user_info : ERROR', error )
        d3.select('.LogError').append('p').style('color','red').text(t('User.Pages.err_get_user_infos'))
        // Increase number of requests
        reqCount_ = reqCount_ + 1
        setReqCount(reqCount_)
      })
    }

    // User's OpenSankey+ licence data
    if (userData.license_opensankeyplus_id === '-') {
      let userData_ = userData
      userData_.loading_opensankeyplus = false
      userData_.license_opensankeyplus_active = t('UserPages.usr_no_lic')
      setUserData(userData_)
    } else {
      if (userData.license_opensankeyplus_id !== '' &&
          userData.loading_opensankeyplus === true &&
          reqCount < 10)
      {
        let reqCount_ = reqCount
        // Get license informations
        checkLicenseOpenSankeyPlus(userData.license_opensankeyplus_id)
        .then(data_edd => {
          // Verify opensankeyplus license validity
          let userData_ = userData
          if (data_edd.success) {
            userData_.license_opensankeyplus_validity = t('UserPages.usr_lic_validdate') + data_edd.expires.substring(0, 10)
            userData_.license_opensankeyplus_active = t('UserPages.usr_lic_valid') // active, inactive, expired, disabled
          }
          else {
            if ( data_edd.license==='invalid' ) {
              userData_.license_opensankeyplus_active =t('UserPages.usr_lic_invalid')
            } else if ( data_edd.license==='expired' ) {
              userData_.license_opensankeyplus_active =t('UserPages.usr_lic_expdate')
              userData_.license_opensankeyplus_validity = data_edd.expires.substring(0, 10)
            } else if ( data_edd.license==='disabled' ) {
              userData_.license_opensankeyplus_active =t('UserPages.usr_lic_deactivated')
            } else {
              userData_.license_opensankeyplus_active =t('UserPages.usr_lic_err')
            }
          }
          userData_.loading_opensankeyplus = false
          setUserData(userData_)
          // Increase number of requests
          reqCount_ = reqCount_ + 1
          setReqCount(reqCount_)
        }).catch( error => {
          // Erreur fetch license
          d3.select('.LogError').append('p').style('color','red').text(t('UserPages.err_get_OS+_infos'))
          console.log( 'check_license OpenSankey+ : ERROR', error )
          // Increase number of requests
          reqCount_ = reqCount_ + 1
          setReqCount(reqCount_)
        })
      }
    }

    // User's SankeySuite licence data
    if (userData.license_sankeysuite_id === '-') {
      let userData_ = userData
      userData_.loading_sankeysuite = false
      userData_.license_sankeysuite_active = t('UserPages.usr_no_lic')
      setUserData(userData_)
    }
    else {
      if (userData.license_sankeysuite_id !== '' &&
          userData.loading_sankeysuite === true &&
          reqCount < 10)
      {
        let reqCount_ = reqCount
        // Get license informations
        checkLicenseSankeySuite(userData.license_sankeysuite_id)
        .then(data_edd => {
          // Verify MFA license validity
          let userData_ = userData
          if (data_edd.success) {
            userData_.license_sankeysuite_validity = t('UserPages.usr_lic_validdate') + data_edd.expires.substring(0, 10)
            userData_.license_sankeysuite_active = t('UserPages.usr_lic_valid') // active, inactive, expired, disabled
          }
          else {
            if ( data_edd.license==='invalid' ) {
              userData_.license_sankeysuite_active =t('UserPages.usr_lic_invalid')
            } else if ( data_edd.license==='expired' ) {
              userData_.license_sankeysuite_active = t('UserPages.usr_lic_expdate')
              userData_.license_sankeysuite_validity = data_edd.expires.substring(0, 10)
            } else if ( data_edd.license==='disabled' ) {
              userData_.license_sankeysuite_active = t('UserPages.usr_lic_deactivated')
            } else {
              userData_.license_sankeysuite_active = t('UserPages.usr_lic_err')
            }
          }
          userData_.loading_sankeysuite = false
          setUserData(userData_)
          // Increase number of requests
          reqCount_ = reqCount_ + 1
          setReqCount(reqCount_)
        }).catch( error => {
          // Erreur fetch license
          d3.select('.LogError').append('p').style('color','red').text(t('UserPages.err_get_SS_infos'))
          console.log( 'check_license SankeySuite : ERROR', error )
          // Increase number of requests
          reqCount_ = reqCount_ + 1
          setReqCount(reqCount_)
        })
      }
    }
  }, [reqCount, userData, t])

  return(
    <div>
      <Navbar className='bg-light navbar-Account' fixed='top' style={{ 'display': 'block' }} >
        <Container className='MenuNavigation'>
          <Navbar.Brand href="/" rel="noopener noreferrer"><img src={navbar_logo} alt='navigation logo' width={navbar_logo_width ? navbar_logo_width : 200} /> {navbar_app_version} </Navbar.Brand>
          <Nav>
            <Nav.Item>
              <Button variant='light' onClick={()=>returnToApp()}>{t('UserPages.to_app')}</Button>
            </Nav.Item>
            <Nav.Item>
              <Button variant='light' onClick={()=>returnToDashboard()}>{t('UserPages.to_dbd')}</Button>
            </Nav.Item>
            <Nav.Item>
              <Button variant='danger' onClick={()=>loginOut()}><FaPowerOff/></Button>
            </Nav.Item>
          </Nav>
        </Container>
      </Navbar>

      <div>
        <Card style={{'marginTop':'85px'}}>
          <Card.Header style={{'textAlign':'left'}}>{t('UserPages.win_acc_infos')}</Card.Header>
          <Card.Body>
            {userData.loading ? (
              <Spinner animation="border" />
            ) : (
              <Form>
                {/* Infos utilisateur  --------------------------------------------------------- */}
                <FormGroup as={Row}>
                  <Col xs={3}>
                    <FormLabel>{t('UserPages.pnom')}</FormLabel>
                  </Col>
                  <Col xs={9}>
                    <FormLabel>{userData.firstname}</FormLabel>
                  </Col>
                </FormGroup>
                <FormGroup as={Row}>
                  <Col xs={3}>
                    <FormLabel>{t('UserPages.nom')}</FormLabel>
                  </Col>
                  <Col xs={9}>
                    <FormLabel>{userData.name}</FormLabel>
                  </Col>
                </FormGroup>
                <FormGroup as={Row} >
                  <Col xs={3}>
                    <FormLabel>{t('UserPages.id')}</FormLabel>
                  </Col>
                  <Col xs={9}>
                    <FormLabel>{userData.id}</FormLabel>
                  </Col>
                </FormGroup>
                {/* Infos licence OpenSankey+  --------------------------------------------------------- */}
                <FormGroup as={Row}>
                  <Col xs={3}>{t('UserPages.OS+_lic')}
                  </Col>
                  <Col xs={2}>
                    <FormControl
                        type="text"
                        onChange={(e) => {
                          setNewLicenseOpenSankeyPlus(e.target.value)
                          setNewLicenseOpenSankeyPlusToCheck(true)}
                        }
                        placeholder={userData.license_opensankeyplus_id} />
                  </Col>
                  <Col xs={1}>
                    <Button
                      variant='light'
                      onClick={()=>signupNewLicenseOpenSankeyPlus()}
                      disabled={newLicenseOpenSankeyPlusToCheck===false}>
                        {t('UserPages.update_lic')}
                    </Button>
                  </Col>
                  {userData.loading_opensankeyplus ? (
                    <>
                    <Col xs={3}>
                      <Spinner animation="border" />
                    </Col>
                    <Col xs={3}>
                      <Spinner animation="border" />
                    </Col>
                    </>
                  ) : (
                    <>
                    <Col xs={3}>
                      <FormLabel>{userData.license_opensankeyplus_active}</FormLabel>
                    </Col>
                    <Col xs={3}>
                      <FormLabel>{userData.license_opensankeyplus_validity}</FormLabel>
                    </Col>
                    </>
                  )}
                </FormGroup>
                {/* Infos licence SankeySuite  --------------------------------------------------------- */}
                <FormGroup as={Row}>
                  <Col xs={3}>
                    <FormLabel>{t('UserPages.SS_lic')}</FormLabel>
                  </Col>
                  <Col xs={2}>
                    <FormControl
                      type="text"
                      onChange={(e) => {
                        setNewLicenseSankeySuite(e.target.value)
                        setNewLicenseSankeySuiteToCheck(true)}
                      }
                      placeholder={userData.license_sankeysuite_id} />
                  </Col>
                  <Col xs={1}>
                    <Button
                      variant='light'
                      onClick={()=>signupNewLicenseSankeySuite()}
                      disabled={newLicenseSankeySuiteToCheck===false}>
                        {t('UserPages.update_lic')}
                    </Button>
                  </Col>
                  {userData.loading_sankeysuite ? (
                    <>
                    <Col xs={3}>
                      <Spinner animation="border" />
                    </Col>
                    <Col xs={3}>
                      <Spinner animation="border" />
                    </Col>
                    </>
                  ) : (
                    <>
                    <Col xs={3}>
                      <FormLabel>{userData.license_sankeysuite_active}</FormLabel>
                    </Col>
                    <Col xs={3}>
                      <FormLabel>{userData.license_sankeysuite_validity}</FormLabel>
                    </Col>
                    </>
                  )}
                </FormGroup>
              </Form>
            )}
            <div className='LogError' style={{'color':'red'}}></div>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

Account.propTypes = AccountPropTypes

export default Account




